import { styled } from '@mui/material/styles';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import Modal from '@/components/Modal/Modal';
import Select from '@/components/Select/Select';
import Button from '@/components/Button/Button';
import FileGrid from '@/components/FileExplorer/FileGrid';
import GameCard from '@/components/GameCard/GameCard';
import {
  fetchGames,
  fetchMarketingAssets,
  postGamesThumbnail,
} from '@/lib/api';
import { GameSchema, MarketingAssetSchema } from '@/lib/types';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import { assetQueryKeys } from '@/lib/assetQueryKeys';

const SelectThumbnailModal = ({
  game,
  open,
  onClose,
}: {
  game: GameSchema;
  open: boolean;
  onClose: () => void;
}) => {
  const queryClient = useQueryClient();
  const rtpInitRef = useRef(false);
  const thumbnailInitRef = useRef(false);
  const thumbnailsScrollRef = useRef<HTMLDivElement>(null);

  const [selectedRtpGameIds, setSelectedRtpGameIds] = useState<
    number[]
  >([]);
  const [selectedThumbnailId, setSelectedThumbnailId] = useState<
    number | null
  >(null);

  const { data: groupGamesData, isLoading } = useQuery({
    queryKey: ['games', 'game_group', game.game_group],
    queryFn: () =>
      fetchGames({
        game_group: game.game_group ? [game.game_group] : undefined,
      }),
    enabled: open && !!game.game_group,
  });

  const groupGames = useMemo(() => {
    if (!isLoading) {
      return groupGamesData?.games || [];
    }
    return [];
  }, [groupGamesData, isLoading]);

  const groupGameIds = useMemo(
    () => groupGames.map((g) => g.id),
    [groupGames],
  );

  const thumbnailsEnabled = open && groupGameIds.length > 0;

  const {
    data: thumbnailAssets,
    isLoading: thumbnailsLoading,
    isFetchingNextPage: thumbnailsFetchingNextPage,
  } = useInfiniteScroll<MarketingAssetSchema>({
    fetchFn: fetchMarketingAssets,
    queryKey: [...assetQueryKeys.marketing_assets.flat, groupGameIds],
    params: { file_types: ['thumbnails'], game_ids: groupGameIds },
    scrollContainerRef: thumbnailsScrollRef,
    useWindowScroll: false,
    pageSize: 20,
    enabled: thumbnailsEnabled,
  });

  const thumbnailsFetched = thumbnailsEnabled && !thumbnailsLoading;

  const { mutateAsync: mutateThumbnailAsync, isPending: isApplying } =
    useMutation({
      mutationFn: ({
        game_id,
        marketing_file_id,
      }: {
        game_id: number;
        marketing_file_id: number;
      }) => postGamesThumbnail(game_id, marketing_file_id),
    });

  useEffect(() => {
    if (!open) {
      rtpInitRef.current = false;
      thumbnailInitRef.current = false;
      setSelectedRtpGameIds([]);
      setSelectedThumbnailId(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || rtpInitRef.current || groupGameIds.length === 0) {
      return;
    }
    console.log(groupGameIds);
    setSelectedRtpGameIds(groupGameIds);
    rtpInitRef.current = true;
  }, [open, groupGameIds]);

  useEffect(() => {
    if (!open || thumbnailInitRef.current || !thumbnailsFetched) {
      return;
    }
    const activeAsset = thumbnailAssets.find(
      (asset) => asset.preview_url === game.photo_url,
    );
    setSelectedThumbnailId(activeAsset ? activeAsset.id : null);
    thumbnailInitRef.current = true;
  }, [open, thumbnailsFetched, thumbnailAssets, game]);

  const rtpOptions = useMemo(
    () =>
      groupGames.map((g) => ({
        id: g.id,
        value: g.id,
        label: g.game_name,
        text: g.game_name,
      })),
    [groupGames],
  );

  const selectedAsset = useMemo(
    () =>
      thumbnailAssets.find(
        (asset) => asset.id === selectedThumbnailId,
      ),
    [thumbnailAssets, selectedThumbnailId],
  );

  const previewGame = useMemo(() => {
    if (!selectedAsset) return null;
    return { ...game, photo_url: selectedAsset.preview_url || '' };
  }, [game, selectedAsset]);

  const showEmptyState =
    thumbnailsFetched && thumbnailAssets.length === 0;

  const canApply =
    selectedThumbnailId != null && selectedRtpGameIds.length > 0;

  const handleClose = () => {
    if (isApplying) return;
    onClose();
  };

  const handleApply = () => {
    if (!canApply || selectedThumbnailId == null) return;

    Promise.all(
      selectedRtpGameIds.map((game_id) =>
        mutateThumbnailAsync({
          game_id,
          marketing_file_id: selectedThumbnailId,
        }),
      ),
    ).then(() => {
      queryClient.invalidateQueries({ queryKey: ['games'] });
      handleClose();
    });
  };

  return (
    <StyledModal
      open={open}
      onClose={handleClose}
      isActionBusy={isApplying}
      title={
        <FormattedMessage
          id="selectThumbnail"
          defaultMessage="Select Thumbnail"
        />
      }
      bodyStyle={MODAL_BODY_STYLE}
      actions={[
        <Button
          key="cancel"
          variant="text"
          onClick={handleClose}
          disabled={isApplying}
          aria-label="Cancel Button"
        >
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </Button>,
        <Button
          key="apply"
          variant="contained"
          onClick={handleApply}
          disabled={!canApply || isApplying}
          loading={isApplying}
          aria-label="Apply Button"
        >
          <FormattedMessage id="apply" defaultMessage="Apply" />
        </Button>,
      ]}
    >
      <Section>
        <Label>
          <FormattedMessage
            id="rtpVersions"
            defaultMessage="RTP Versions"
          />
        </Label>
        <Select
          multiple
          placeholder={
            <FormattedMessage id="select" defaultMessage="Select" />
          }
          options={rtpOptions}
          value={selectedRtpGameIds}
          onChange={(v) => setSelectedRtpGameIds(v as number[])}
          variant="outlined"
          searchEnabled
          size="small"
          aria-label="RTP Versions"
        />
      </Section>

      {showEmptyState ? (
        <EmptyState>
          <FormattedMessage
            id="noThumbnailsUploaded"
            defaultMessage="No thumbnails uploaded for this game yet. Upload an image with Asset Type 'Thumbnails' in Marketing Assets first."
          />
        </EmptyState>
      ) : (
        <TwoColumnLayout>
          <GridColumn>
            <Label>
              <FormattedMessage
                id="thumbnail"
                defaultMessage="Thumbnail"
              />
            </Label>
            <ScrollableGrid ref={thumbnailsScrollRef}>
              <FileGrid
                loading={
                  thumbnailsLoading || thumbnailsFetchingNextPage
                }
                files={thumbnailAssets}
                selected={
                  selectedThumbnailId != null
                    ? [selectedThumbnailId]
                    : []
                }
                onSelect={(ids) =>
                  setSelectedThumbnailId(ids[0] ?? null)
                }
                type="marketing"
                selectionMode="radio"
              />
            </ScrollableGrid>
          </GridColumn>
          {previewGame && (
            <PreviewColumn>
              <Label>
                <FormattedMessage
                  id="preview"
                  defaultMessage="Preview"
                />
              </Label>
              <PreviewCardWrapper>
                <GameCard
                  games={[previewGame]}
                  defaultGame={previewGame}
                  section="admin_select_thumbnail_preview"
                  showDownload={false}
                />
              </PreviewCardWrapper>
            </PreviewColumn>
          )}
        </TwoColumnLayout>
      )}
    </StyledModal>
  );
};

const StyledModal = styled(Modal)({
  width: '90%',
  maxWidth: '1254px',
});

// Lets the grid/preview columns manage their own internal scrolling
// (see ScrollableGrid/PreviewColumn below) instead of the modal body.
const MODAL_BODY_STYLE = {
  overflowY: 'hidden',
  display: 'flex',
  flexDirection: 'column',
} as const;

const Section = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const Label = styled('div')(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  marginBottom: theme.spacing(1),
}));

const EmptyState = styled('div')(({ theme }) => ({
  fontSize: '14px',
  lineHeight: '20px',
  color: theme.palette.textSecondaryDarkGrey,
  padding: `${theme.spacing(4)} 0`,
  textAlign: 'center',
}));

const TwoColumnLayout = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(4),
  alignItems: 'stretch',
  overflow: 'hidden',
}));

const GridColumn = styled('div')({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
});

const ScrollableGrid = styled('div')({
  overflowY: 'auto',
});

const PreviewColumn = styled('div')({
  flexShrink: 0,
  overflow: 'hidden',
});

const PreviewCardWrapper = styled('div')({
  pointerEvents: 'none',
});

export default SelectThumbnailModal;
